import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../utils/roles';
import { apiList } from '../api/apiClient';

const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

interface DashboardStats {
  dealers: number;
  customers: number;
  properties: number;
  units: number;
  devices: number;
  people: number;
  webUsers: number;
  monthlyRevenue: number;
  stripeSubscriptions: number;
  enabledFeatures: number;
  totalFeatures: number;
}

interface CellularStats {
  simCount: number;
  consumedMB: number;
  capMB: number;
}

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [cellular, setCellular] = useState<CellularStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auth guard — only super_admin reaches this page
  useEffect(() => {
    if (user && !isSuperAdmin(user.role as any)) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const [dealers, customers, properties, units, devices, people, users, products, features] = await Promise.all([
          apiList('integrators').catch(() => []),
          apiList('customers').catch(() => []),
          apiList('properties').catch(() => []),
          apiList('units').catch(() => []),
          apiList('devices').catch(() => []),
          apiList('tenants').catch(() => []),
          apiList('users').catch(() => []),
          fetch(`${API}/billing-products`, { headers: authHeaders() }).then(r => r.json()).catch(() => []),
          fetch(`${API}/billing-features`, { headers: authHeaders() }).then(r => r.json()).catch(() => []),
        ]);
        const props = Array.isArray(properties) ? properties : (properties as any).items || [];
        const monthly = props.reduce((s: number, p: any) => s + (Number(p.total_monthly_revenue) || 0), 0);
        const stripeSubs = props.filter((p: any) => p.stripe_subscription_id).length;
        const allFeatures = Array.isArray(features) ? features : (features as any).items || [];
        const enabled = allFeatures.filter((f: any) => f.default_enabled !== false).length;
        setStats({
          dealers: (Array.isArray(dealers) ? dealers : (dealers as any).items || []).length,
          customers: (Array.isArray(customers) ? customers : (customers as any).items || []).length,
          properties: props.length,
          units: (Array.isArray(units) ? units : (units as any).items || []).length,
          devices: (Array.isArray(devices) ? devices : (devices as any).items || []).length,
          people: (Array.isArray(people) ? people : (people as any).items || []).length,
          webUsers: (Array.isArray(users) ? users : (users as any).items || []).length,
          monthlyRevenue: monthly,
          stripeSubscriptions: stripeSubs,
          enabledFeatures: enabled,
          totalFeatures: allFeatures.length,
        });
      } catch (e: any) {
        setError(e.message ?? String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
    // Fetch cellular stats in parallel — non-blocking, hide the card on error.
    (async () => {
      try {
        const [simsRes, groupsRes] = await Promise.all([
          fetch(`${API}/cellular/telnyx/sim-cards?page_size=100`, { headers: authHeaders() }),
          fetch(`${API}/cellular/telnyx/sim-card-groups`, { headers: authHeaders() }),
        ]);
        if (!simsRes.ok || !groupsRes.ok) return;
        const sims = (await simsRes.json()).data ?? [];
        const groups = (await groupsRes.json()).data ?? [];
        const mb = (m: any) => m ? (m.unit === 'GB' ? Number(m.amount) * 1024 : Number(m.amount)) : 0;
        const consumedMB = sims.reduce((s: number, x: any) => s + mb(x.current_billing_period_consumed_data), 0);
        const capMB = groups.reduce((s: number, g: any) => s + mb(g.data_limit), 0);
        setCellular({ simCount: sims.length, consumedMB, capMB });
      } catch { /* non-fatal — Cellular card simply won't render */ }
    })();
  }, []);

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading admin dashboard…</div>;

  return (
    <div className="shadow-page-frame space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Platform-wide controls and overview</p>
      </div>

      {error && (
        <Card>
          <div className="p-4 text-red-700 bg-red-50">{error}</div>
        </Card>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Dealers"      value={stats.dealers}      onClick={() => navigate('/integrators')} />
            <Stat label="Customers"    value={stats.customers}    onClick={() => navigate('/customers')} />
            <Stat label="Properties"   value={stats.properties}   onClick={() => navigate('/properties')} />
            <Stat label="Units"        value={stats.units} />
            <Stat label="Devices"      value={stats.devices}      onClick={() => navigate('/devices')} />
            <Stat label="People"       value={stats.people}       onClick={() => navigate('/tenants')} />
            <Stat label="Web Users"    value={stats.webUsers}     onClick={() => navigate('/web-users')} />
            <Stat label="Monthly Rev"  value={`$${stats.monthlyRevenue.toLocaleString()}`} />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <div className="p-4 border-b">
                <h3 className="font-semibold">Stripe Billing</h3>
                <p className="text-xs text-gray-500">Subscription health</p>
              </div>
              <div className="p-4 space-y-2">
                <Row label="Active subscriptions"    value={stats.stripeSubscriptions} />
                <Row label="Properties without sub"  value={stats.properties - stats.stripeSubscriptions} warn={stats.properties - stats.stripeSubscriptions > 0} />
                <div className="pt-2 flex gap-2">
                  <Button variant="secondary" onClick={() => navigate('/billing')}>Open Billing Center →</Button>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-4 border-b">
                <h3 className="font-semibold">Feature Catalog</h3>
                <p className="text-xs text-gray-500">Metered/licensed features</p>
              </div>
              <div className="p-4 space-y-2">
                <Row label="Enabled by default" value={`${stats.enabledFeatures} / ${stats.totalFeatures}`} />
                <div className="pt-2 flex gap-2">
                  <Button variant="secondary" onClick={() => navigate('/billing')}>Manage Catalog →</Button>
                  <Button variant="secondary" onClick={() => navigate('/permissions')}>Permissions →</Button>
                </div>
              </div>
            </Card>
          </div>

          <Card>
            <div className="p-4 border-b">
              <h3 className="font-semibold">Quick actions</h3>
            </div>
            <div className="p-4 grid md:grid-cols-3 gap-3">
              <ActionTile label="Run /counters/refresh" onClick={async () => { await fetch(`${API}/counters/refresh`, { method:'POST', headers: authHeaders() }); alert('Counters refreshed.'); }} />
              <ActionTile label="Backfill subscriptions" onClick={async () => { const r = await fetch(`${API}/billing/backfill-subscriptions`, { method:'POST', headers: authHeaders() }); const d = await r.json(); alert(`provisioned=${d.provisioned}  skipped=${d.skipped}  failed=${d.failed}`); }} />
              <ActionTile label="Inbox (admin@intrlock.io)"  onClick={() => navigate('/admin/inbox')} />
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number | string; onClick?: () => void }> = ({ label, value, onClick }) => (
  <div onClick={onClick} className={`bg-white rounded-lg border border-gray-200 p-4 ${onClick ? 'cursor-pointer hover:border-gray-300' : ''}`}>
    <div className="text-xs uppercase text-gray-500">{label}</div>
    <div className="text-2xl font-semibold mt-1">{value}</div>
  </div>
);

const Row: React.FC<{ label: string; value: number | string; warn?: boolean }> = ({ label, value, warn }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-600">{label}</span>
    {warn ? <Badge color="orange">{value}</Badge> : <span className="font-semibold">{value}</span>}
  </div>
);

const ActionTile: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button onClick={onClick} className="text-left p-3 rounded border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition">
    <div className="text-sm font-medium">{label}</div>
  </button>
);

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}`,
  };
}

function formatCellularMB(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
}
